import { createRouter, createWebHistory, RouterView } from 'vue-router';
import gql from 'graphql-tag';
// eslint-disable-next-line import/no-cycle
import store from '@/store';
import { apolloClient } from '@/vue-apollo';
import MainLayout from '@/layouts/Main';
import Home from '@/views/Home';
import Login from '@/views/Login';
import Products from '@/views/Products';
import ProductsCars from '@/views/ProductsCars';
import ProductsCar from '@/views/ProductsCar';
import ProductsCarVisualise from '@/views/ProductsCarVisualise';
import ProductsDevices from '@/views/ProductsDevices';
import ProductsDevice from '@/views/ProductsDevice';
import Analytics from '@/views/Analytics';
import AnalyticsVolumes from '@/views/AnalyticsVolumes';
import Users from '@/views/Users';
import UsersAdd from '@/views/UsersAdd';
import UsersAddCompany from '@/views/UsersAddCompany';
import UsersEdit from '@/views/UsersEdit';
import EditProfile from '@/views/EditProfile';
import EditCompany from '@/views/EditCompany';
import EditCompanyBillingDetails from '@/views/EditCompanyBillingDetails';
import EditCompanyEnabledFeatures from '@/views/EditCompanyEnabledFeatures';
import EditCompanyUserLimit from '@/views/EditCompanyUserLimit';
import EditCompanyIssueRenewal from '@/views/EditCompanyIssueRenewal';
import CompanyDisabled from '@/views/CompanyDisabled';
import Dashboards from '@/views/Dashboards';
import DashboardsCharts from '@/views/DashboardsCharts';
import ImportSourceCars from '@/views/ImportSourceCars';
import Test from '@/views/Test';

async function getUserData(username) {
  return (await apolloClient.query({
    query: gql`
      query ($username: String!) {
        users_by_pk(id: $username) {
          update_ref_id
          selected_cars
        }
      }
    `,
    variables: {
      username,
    },
  })).data.users_by_pk;
}

const routes = [
  {
    path: '/',
    component: MainLayout,
    children: [
      {
        path: '',
        component: Home,
        name: 'Home',
        meta: {
          hero: {
            title() {
              const companyName = store.state.user.user?.company?.name;
              const who = companyName || store.state.user.user.givenName;
              return `${who}'s LightingMI`;
            },
            lead: 'Start predicting the future with your comprehensive, data-oriented LightingMI account.',
          },
        },
      },
      {
        path: 'login',
        component: Login,
        name: 'Login',
      },
      {
        path: 'products',
        component: RouterView,
        meta: {
          breadcrumbs: [
            {
              label: 'Products',
              to: { name: 'Products' },
            },
          ],
        },
        children: [
          {
            path: '',
            component: Products,
            name: 'Products',
            meta: {
              hero: {
                title: 'Products',
                lead: 'Explore car and device data and how they\'re put together.',
              },
            },
          },
          {
            path: 'cars',
            name: 'Products/Cars',
            component: ProductsCars,
            meta: {
              breadcrumbs: [
                {
                  label: 'Cars',
                  to: {
                    name: 'Products/Cars',
                  },
                },
              ],
            },
          },
          {
            path: 'car/:carId',
            component: RouterView,
            meta: {
              breadcrumbs: async ($route) => {
                const { data } = await apolloClient.query({
                  query: gql`
                    query {
                      cars(where: {id: {_eq: ${$route.params.carId}}}) {
                        id
                        model
                      }
                    }
                  `,
                });

                return [
                  {
                    label: 'Cars',
                    to: { name: 'Products/Cars' },
                  },
                  {
                    label: data.cars[0].model,
                    to: {
                      name: 'Products/Car',
                      params: {
                        carId: data.cars[0].id,
                      },
                    },
                  },
                ];
              },
            },
            children: [
              {
                path: '',
                component: ProductsCar,
                name: 'Products/Car',
              },
              {
                path: 'visualise',
                component: ProductsCarVisualise,
                name: 'Products/Car/Visualise',
                meta: {
                  breadcrumbs($route) {
                    return [
                      {
                        label: 'Visualise',
                        to: {
                          name: 'Products/Car/Visualise',
                          params: {
                            carId: $route.params.carId,
                          },
                        },
                      },
                    ];
                  },
                },
              },
            ],
          },
          {
            path: 'device/:deviceId',
            component: ProductsDevice,
            name: 'Products/Device',
            meta: {
              breadcrumbs: async ($route) => {
                const { data } = await apolloClient.query({
                  query: gql`
                    query($id: Int!) {
                      res: lighting_devices_by_pk(id: $id) {
                        part_number
                      }
                    }
                  `,
                  variables: {
                    id: $route.params.deviceId,
                  },
                });

                return [
                  {
                    label: 'Lighting Devices',
                    to: { name: 'Products/Devices' },
                  },
                  {
                    label: data.res.part_number,
                    to: {
                      name: 'Products/Device',
                      params: {
                        deviceId: $route.params.deviceId,
                      },
                    },
                  },
                ];
              },
            },
          },
          {
            path: 'devices',
            component: ProductsDevices,
            name: 'Products/Devices',
            meta: {
              breadcrumbs: [
                {
                  label: 'Lighting Devices',
                  to: { name: 'Products/Devices' },
                },
              ],
            },
          },
        ],
      },
      {
        path: 'analytics',
        component: RouterView,
        meta: {
          breadcrumbs: [
            {
              label: 'Analytics',
              to: { name: 'Analytics' },
            },
          ],
        },
        children: [
          {
            path: '',
            component: Analytics,
            name: 'Analytics',
            meta: {
              hero: {
                title: 'Analytics',
                lead: 'Uncover cars and LDV insights with our data-rich visual tools.',
              },
            },
          },
          ...[
            {
              type: 'Cars',
              route: {
                name: 'Analytics/CarVolumes',
                path: 'car-volumes',
              },
              label: 'Car Volumes',
            },
            {
              type: 'LightingDevices',
              route: {
                name: 'Analytics/LightingDeviceVolumes',
                path: 'lighting-device-volumes',
              },
              label: 'Lighting Device Volumes',
            },
          ].map((volumesTypeData) => ({
            path: volumesTypeData.route.path,
            component: AnalyticsVolumes,
            props: () => ({ volumesType: volumesTypeData.type }),
            name: volumesTypeData.route.name,
            meta: {
              breadcrumbs: [
                {
                  label: volumesTypeData.label,
                  to: { name: volumesTypeData.route.name },
                },
              ],
            },
          })),
        ],
      },
      {
        path: 'dashboards',
        component: RouterView,
        meta: {
          breadcrumbs: [
            {
              label: 'Dashboards',
              to: { name: 'Dashboards' },
            },
          ],
        },
        children: [
          {
            path: '',
            component: Dashboards,
            name: 'Dashboards',
            meta: {
              hero: {
                title: 'Dashboards',
                lead: 'Monitor key insights with personalised dashboards.',
              },
            },
          },
          {
            path: ':dashboardId',
            component: DashboardsCharts,
            name: 'Dashboards/Charts',
            meta: {
              breadcrumbs: [
                async ($route) => {
                  const { dashboardId } = $route.params;
                  const res = await apolloClient.query({
                    query: gql`
                      query($id: Int!) {
                        dashboards_by_pk(id: $id) {
                          id
                          name
                        }
                      }
                    `,
                    variables: {
                      id: dashboardId,
                    },
                  });

                  return {
                    label: res.data.dashboards_by_pk.name || 'Untitled',
                    to: {
                      name: 'Dashboards/Charts',
                      params: {
                        dashboardId,
                      },
                    },
                  };
                },
              ],
            },
          },
        ],
      },
      {
        path: 'users',
        component: RouterView,
        meta: {
          breadcrumbs: [
            {
              label: 'User Management',
              to: { name: 'Users' },
            },
          ],
        },
        children: [
          {
            path: '',
            component: Users,
            name: 'Users',
          },
          {
            path: 'add-user',
            component: UsersAdd,
            name: 'Users/Add',
            meta: {
              breadcrumbs: [
                {
                  label: 'Add',
                  to: { name: 'Users/Add' },
                },
              ],
            },
          },
          {
            path: 'add-company',
            component: UsersAddCompany,
            name: 'Users/AddCompany',
            meta: {
              breadcrumbs: [
                {
                  label: 'Add Company',
                  to: { name: 'Users/AddCompany' },
                },
              ],
            },
          },
          {
            path: 'edit/:userId',
            component: UsersEdit,
            name: 'Users/Edit',
            meta: {
              breadcrumbs: ($route) => [
                {
                  label: 'Edit',
                  to: { name: 'Users/Edit', params: { userId: $route.params.userId } },
                },
              ],
            },
          },
        ],
      },
      {
        path: 'import',
        component: RouterView,
        meta: {
          breadcrumbs: [
            {
              label: 'Source cars Management',
              to: { name: 'Import/SourceCars' },
            },
          ],
        },
        children: [
          {
            path: '',
            component: ImportSourceCars,
            name: 'Import/SourceCars',
          },
        ],
      },
      {
        path: 'edit-profile',
        name: 'EditProfile',
        component: EditProfile,
        meta: {
          breadcrumbs: [
            {
              label: 'Edit Profile',
              to: { name: 'EditProfile' },
            },
          ],
        },
      },
      {
        path: 'edit-company/:companyId',
        component: RouterView,
        meta: {
          breadcrumbs($route) {
            return [
              {
                label: 'Edit Company',
                to: { name: 'EditCompany', params: { companyId: $route.params.companyId } },
              },
            ];
          },
        },
        children: [
          {
            path: '',
            name: 'EditCompany',
            component: EditCompany,
          },
          {
            path: 'billing-details',
            name: 'EditCompany/BillingDetails',
            component: EditCompanyBillingDetails,
            meta: {
              breadcrumbs($route) {
                return [
                  {
                    label: 'Billing Details',
                    to: { name: 'EditCompany/BillingDetails', params: { companyId: $route.params.companyId } },
                  },
                ];
              },
            },
          },
          {
            path: 'enabled-features',
            name: 'EditCompany/EnabledFeatures',
            component: EditCompanyEnabledFeatures,
            meta: {
              breadcrumbs($route) {
                return [
                  {
                    label: 'Enabled Features',
                    to: { name: 'EditCompany/EnabledFeatures', params: { companyId: $route.params.companyId } },
                  },
                ];
              },
            },
          },
          {
            path: 'user-limit',
            name: 'EditCompany/UserLimit',
            component: EditCompanyUserLimit,
            meta: {
              breadcrumbs($route) {
                return [
                  {
                    label: 'Change user limit',
                    to: { name: 'EditCompany/UserLimit', params: { companyId: $route.params.companyId } },
                  },
                ];
              },
            },
          },
          {
            path: 'issue-renewal',
            name: 'EditCompany/IssueRenewal',
            component: EditCompanyIssueRenewal,
            meta: {
              breadcrumbs($route) {
                return [
                  {
                    label: 'Issue a Renewal',
                    to: { name: 'EditCompany/IssueRenewal', params: { companyId: $route.params.companyId } },
                  },
                ];
              },
            },
          },
        ],
      },
      {
        path: '/no-access',
        name: 'NoAccess',
        component: CompanyDisabled,
      },
    ],
  },
  {
    path: '/logout',
    name: 'Logout',
    async beforeEnter() {
      await store.dispatch('user/logOut');
      window.location.href = '/';
    },
  },
  {
    path: '/test',
    component: MainLayout,
    children: [
      {
        path: '',
        name: 'Test',
        component: Test,
      },
    ],
  },
];

const publicRoutes = ['Login', 'Logout', 'NoAccess'];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

router.beforeEach(async (to) => {
  await store.state.app.initializingPromise;
  const isPublicRoute = publicRoutes.includes(to.name);
  const isJwtTokenValid = new Date(store.getters['user/jwtTokenExp'] * 1000) > Date.now();
  if ((!store.getters['user/isLoggedIn'] && !isPublicRoute) || (!isJwtTokenValid && !isPublicRoute)) {
    await store.dispatch('user/logOut');
    return true;
  }

  if (!store.getters['user/isAdmin'] && !store.getters['user/isCompanyHasAccess'] && !isPublicRoute
    && !store.getters['user/isCompanyAdmin']) {
    return { name: 'NoAccess' };
  }

  if (!store.getters['app/isUpdateReferenceExist'] && store.getters['user/isLoggedIn']) {
    const selectionData = await getUserData(store.getters['user/username']);
    const updateReferenceId = selectionData.update_ref_id;
    const selectedCars = JSON.parse(selectionData.selected_cars);

    await store.dispatch('app/referenceId', { updateReferenceId });
    await store.dispatch('app/selectedCars', { carsId: selectedCars });
  }

  return true;
});

export default router;
